import { t } from '../i18n';
import { Icon } from '../icons';
import { controlClass } from '../styles';
import type { CheckState } from './password';

/**
 * A password input with a show/hide button inside its right edge (44 px target). The
 * screen owns `visible`, so it can hide every password again on submit.
 */
export function PasswordInput({
  id,
  value,
  onChange,
  visible,
  onVisibleChange,
  autoComplete,
  invalid = false,
  describedBy,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  autoComplete: 'current-password' | 'new-password';
  invalid?: boolean;
  describedBy?: string | undefined;
}) {
  return (
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        autoCapitalize="none"
        spellCheck={false}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={invalid}
        aria-describedby={describedBy || undefined}
        className={`${controlClass} pr-12`}
      />
      <button
        type="button"
        // A fixed name plus aria-pressed: screen readers say "Mostrar contraseña, pressed".
        aria-label={t('password.show')}
        aria-pressed={visible}
        aria-controls={id}
        onClick={() => onVisibleChange(!visible)}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-muted hover:text-ink focus-visible:outline-offset-[-3px]"
      >
        <Icon name={visible ? 'eyeOff' : 'eye'} />
      </button>
    </div>
  );
}

export interface Check {
  readonly key: string;
  readonly state: CheckState;
  readonly label: string;
}

const checkIcons = { pending: 'pending', ok: 'check', problem: 'cross' } as const;

/**
 * Live checks under a password field. Each line says its state with an icon and in
 * words for screen readers. Goes in a Field hint (with hintId), so the control reads
 * these lines as its description; no aria-label for the same reason.
 */
export function CheckList({ checks }: { checks: readonly Check[] }) {
  return (
    <ul className="grid gap-1.5">
      {checks.map((check) => (
        <li
          key={check.key}
          data-state={check.state}
          className={`flex items-start gap-2 text-[13.5px] leading-5 ${
            check.state === 'pending' ? 'text-muted' : 'text-ink'
          } ${check.state === 'problem' ? 'font-medium' : ''}`}
        >
          <span className="mt-px">
            <Icon name={checkIcons[check.state]} size={18} />
          </span>
          <span>{check.label}</span>
          <span className="sr-only"> ({t(`account.password.checks.state.${check.state}`)})</span>
        </li>
      ))}
    </ul>
  );
}

/** One polite summary for every check on the screen: progress, not keystrokes. */
export function ChecksSummary({ checks, active }: { checks: readonly Check[]; active: boolean }) {
  const done = checks.filter((check) => check.state === 'ok').length;
  return (
    <p aria-live="polite" className="sr-only">
      {active ? t('account.password.checks.summary', { done, total: checks.length }) : ''}
    </p>
  );
}
